/**
 * Flota — pulpit i alerty (`fleet.web.dash.*`, `fleet.web.alerts.*`), doklejone przez `extra.ts`.
 * Widoki analityczne: `fleet-analytics-views.ts`; trasy i integracja: `fleet-routes.ts`.
 * `fleetAnalyticsEn` jest typowany względem `fleetAnalyticsPl` — brak klucza = błąd kompilacji.
 */
type Shape<T> = { [K in keyof T]: T[K] extends string ? string : Shape<T[K]> };

export const fleetAnalyticsPl = {
  dash: {
    title: 'Pulpit floty',
    description: 'Stan floty, zaległości do weryfikacji, terminy i alerty w jednym miejscu.',
    chipAlerts: 'Alerty otwarte',
    chipCritical: 'Krytyczne',
    asOf: 'Stan na {time}',
    refresh: 'Odśwież',
    loadFailed: 'Nie udało się wczytać pulpitu floty.',
    filters: {
      vehicle: 'Pojazd',
      allVehicles: 'Wszystkie pojazdy',
      group: 'Grupa kierowców',
      allGroups: 'Wszystkie grupy',
      type: 'Typ pojazdu',
      allTypes: 'Wszystkie typy',
      due: 'Terminy w ciągu',
      missing: 'Brak karty z ostatnich',
      days: '{n} dni',
      driver: 'Kierowca',
      allDrivers: 'Wszyscy kierowcy',
      from: 'Od',
      to: 'Do',
      category: 'Kategoria kosztu',
      allCategories: 'Wszystkie kategorie',
      note: 'Kierowca, okres i kategoria kosztu zawężają kolejki do weryfikacji, zgłoszenia i alerty (terminy i limity zależą od stanu pojazdu). Pełną analizę ustawisz w Analityce.',
    },
    tiles: {
      active: 'Pojazdy aktywne',
      inService: 'Pojazdy w serwisie',
      decommissioned: 'Pojazdy wyłączone',
      pendingLogbooks: 'Karty drogowe do weryfikacji',
      missingLogbooks: 'Brakujące karty drogowe',
      fuelPending: 'Tankowania do weryfikacji',
      expensesPending: 'Koszty do weryfikacji',
      leasesNearLimit: 'Leasingi blisko limitu km',
      dueDates: 'Przeglądy i ubezpieczenia',
      incidents: 'Otwarte usterki i szkody',
      elevatedCosts: 'Podwyższone koszty',
      integration: 'Błędy integracji tras',
    },
    hints: {
      fuelWarnings: '{count} z ostrzeżeniami',
      expensesAmount: 'na kwotę {amount}',
      dueOverdue: '{count} po terminie',
      incidentsSerious: '{count} poważnych',
      incidentsDecision: '{count} czeka na decyzję',
      integrationNever: 'Import tras nie był jeszcze uruchamiany',
      integrationFailed: '{count} nieudanych przebiegów (7 dni)',
      integrationUnmapped: 'Trasy bez pojazdu: {count}',
      noLeasesAccess: 'Brak dostępu do umów',
      discrepancy: '{count} niezgodnych',
      oldest: 'najstarsze od {date}',
    },
    sections: {
      missing: 'Brakujące karty drogowe',
      missingHint: 'Pojazdy, których licznik wzrósł bez pokrycia kartą drogową (okno: {days} dni).',
      missingEmpty: 'Wszystkie przebiegi z ostatnich dni mają karty drogowe.',
      missingKm: '{km} bez karty',
      due: 'Terminy przeglądów i ubezpieczeń',
      dueHint: 'Terminy w ciągu {days} dni oraz po terminie.',
      dueEmpty: 'Brak zbliżających się terminów.',
      dueKind: { INSPECTION: 'Przegląd', INSURANCE: 'Ubezpieczenie' },
      dueLeft: 'za {n} dni',
      dueToday: 'dziś',
      dueOverdue: 'po terminie o {n} dni',
      leases: 'Pojazdy blisko limitu leasingu',
      leasesHint: 'Umowy z przekroczonym progiem ostrzegawczym.',
      leasesEmpty: 'Żadna umowa nie przekroczyła progu ostrzegawczego.',
      leasesForecast: 'Prognoza przekroczenia: {km}',
      incidents: 'Usterki i szkody',
      incidentsEmpty: 'Brak otwartych zgłoszeń.',
      elevated: 'Pojazdy o podwyższonych kosztach',
      elevatedHint: 'Koszt eksploatacji na km (bez raty leasingu) powyżej 1,4 × mediany grupy, ostatnie 90 dni.',
      elevatedEmpty: 'Żaden pojazd nie odbiega kosztami od grupy.',
      elevatedRatio: '{ratio}× mediana ({median})',
      integration: 'Integracja tras',
      integrationHint: 'Import pliku CSV — system tras nie jest jeszcze podłączony.',
      integrationLast: 'Ostatni przebieg: {date}',
      integrationNever: 'Import nie był jeszcze uruchamiany.',
      integrationRejected: 'Odrzucone wiersze (7 dni): {count}',
      integrationOpen: 'Przejdź do integracji',
    },
  },
  alerts: {
    title: 'Alerty floty',
    description: 'Wykryte problemy z uzasadnieniem i sugerowaną czynnością. Alert zamknięty ręcznie nie wraca przez 14 dni.',
    empty: 'Brak alertów — flota w porządku.',
    emptyFiltered: 'Żaden alert nie pasuje do filtrów.',
    loadFailed: 'Nie udało się wczytać alertów.',
    filterStatus: 'Status alertu',
    filterKind: 'Kategoria',
    filterSeverity: 'Ważność',
    allStatuses: 'Wszystkie statusy',
    allKinds: 'Wszystkie kategorie',
    allSeverities: 'Każda ważność',
    status: { OPEN: 'Otwarty', ACK: 'Potwierdzony', RESOLVED: 'Zamknięty' },
    severity: { CRITICAL: 'Krytyczny', WARNING: 'Ostrzeżenie', INFO: 'Informacja' },
    kind: {
      LEASE_LIMIT: 'Limit km leasingu',
      LEASE_FORECAST_OVERAGE: 'Prognoza przekroczenia limitu',
      INSPECTION_DUE: 'Termin przeglądu',
      INSURANCE_DUE: 'Termin ubezpieczenia',
      MISSING_LOGBOOK: 'Brak karty drogowej',
      PENDING_LOGBOOK_REVIEW: 'Karty czekają na weryfikację',
      PENDING_FUEL_REVIEW: 'Tankowania czekają na weryfikację',
      PENDING_EXPENSE_REVIEW: 'Koszty czekają na weryfikację',
      INCIDENT_OPEN: 'Otwarte zgłoszenie',
      ELEVATED_COST: 'Podwyższone koszty',
      DRIVER_HOURS_OVER_LIMIT: 'Kierowca ponad próg godzin',
    },
    actions: {
      REVIEW_LEASE_USAGE: 'Sprawdź wykorzystanie limitu i zaplanuj trasy',
      PLAN_LEASE_ANNEX: 'Rozważ aneks do umowy leasingowej',
      SCHEDULE_INSPECTION: 'Umów przegląd techniczny',
      RENEW_INSURANCE: 'Odnów ubezpieczenie',
      COMPLETE_LOGBOOK: 'Uzupełnij kartę drogową za ten przebieg',
      REVIEW_LOGBOOKS: 'Zweryfikuj oczekujące karty drogowe',
      REVIEW_FUEL: 'Zweryfikuj oczekujące tankowania',
      REVIEW_EXPENSES: 'Zweryfikuj oczekujące koszty',
      DECIDE_INCIDENT: 'Podejmij decyzję, czy pojazd może jeździć',
      HANDLE_INCIDENT: 'Podejmij zgłoszenie do obsługi',
      INVESTIGATE_COSTS: 'Sprawdź przyczynę wysokich kosztów pojazdu',
      REVIEW_DRIVER_HOURS: 'Sprawdź grafik kierowcy i rozłóż trasy',
    },
    suggested: 'Sugerowana czynność',
    detectedAt: 'Wykryto {date}',
    period: 'Okres: {from} – {to}',
    showEvidence: 'Dane uzasadniające',
    hideEvidence: 'Ukryj dane',
    evidenceTitle: 'Dlaczego ten alert',
    related: 'Powiązane pozycje',
    moreRelated: 'i {count} więcej',
    open: 'Przejdź do szczegółów',
    openVehicle: 'Karta pojazdu',
    noLink: 'Brak dostępu do widoku szczegółów.',
    ack: 'Potwierdź',
    acked: 'Potwierdzony {date}',
    resolve: 'Zamknij',
    resolved: 'Zamknięty {date}',
    ackDone: 'Alert potwierdzony.',
    resolveDone: 'Alert zamknięty.',
    resolveTitle: 'Zamknąć alert?',
    resolveBody: 'Alert zostanie zamknięty ręcznie i nie wróci przez 14 dni, nawet jeśli problem nadal występuje. Zamknij tylko, gdy sprawa jest załatwiona lub świadomie ją pomijasz.',
    resolveConfirm: 'Zamknij alert',
    recompute: 'Przelicz alerty',
    recomputeDone: 'Alerty przeliczone: nowe {created}, odświeżone {updated}, zamknięte {resolved}.',
    pageOf: 'Alerty: {total}',
    evidence: {
      hours: 'Godziny w miesiącu',
      threshold: 'Próg godzin',
      overHours: 'Ponad próg',
      contractNumber: 'Umowa',
      usedPercent: 'Wykorzystanie limitu',
      thresholdReached: 'Osiągnięty próg',
      drivenKm: 'Przejechane km',
      limitKm: 'Limit km',
      remainingKm: 'Pozostało km',
      exceededKm: 'Przekroczenie km',
      forecastEndKm: 'Prognoza przebiegu na koniec umowy',
      forecastOverageKm: 'Prognozowane przekroczenie',
      forecastOverageCost: 'Prognozowany koszt przekroczenia',
      indicative: 'Prognoza orientacyjna',
      dueAt: 'Termin',
      daysLeft: 'Dni do terminu',
      overdue: 'Po terminie',
      undocumentedKm: 'Przebieg bez karty',
      windowDays: 'Okno analizy',
      count: 'Liczba pozycji',
      oldestAgeDays: 'Najstarsza czeka',
      thresholdDays: 'Próg alertu',
      totalAmount: 'Łączna kwota',
      reasons: 'Powód',
      incidentType: 'Rodzaj',
      severity: 'Ważność zgłoszenia',
      status: 'Status zgłoszenia',
      decision: 'Decyzja o pojeździe',
      blocksVehicle: 'Blokuje pojazd',
      costPerKm: 'Koszt na km',
      peerMedianPerKm: 'Mediana grupy na km',
      ratio: 'Krotność mediany',
      km: 'Przebieg w oknie',
      peerGroup: 'Grupa porównawcza',
    },
    reasons: { NO_DECISION: 'Brak decyzji o pojeździe', UNHANDLED: 'Nieobsłużone zgłoszenie' },
    peer: { TYPE: 'pojazdy tego samego typu', FLEET: 'cała flota' },
  },
};

export const fleetAnalyticsEn: Shape<typeof fleetAnalyticsPl> = {
  dash: {
    title: 'Fleet dashboard',
    description: 'Fleet status, review backlog, due dates and alerts in one place.',
    chipAlerts: 'Open alerts',
    chipCritical: 'Critical',
    asOf: 'As of {time}',
    refresh: 'Refresh',
    loadFailed: 'Could not load the fleet dashboard.',
    filters: {
      vehicle: 'Vehicle',
      allVehicles: 'All vehicles',
      group: 'Driver group',
      allGroups: 'All groups',
      type: 'Vehicle type',
      allTypes: 'All types',
      due: 'Due within',
      missing: 'Missing logbook, last',
      days: '{n} days',
      driver: 'Driver',
      allDrivers: 'All drivers',
      from: 'From',
      to: 'To',
      category: 'Cost category',
      allCategories: 'All categories',
      note: 'Driver, period and cost category narrow the review queues, incidents and alerts (due dates and limits reflect the current state of the vehicle). Full analysis is in Analytics.',
    },
    tiles: {
      active: 'Active vehicles',
      inService: 'Vehicles in service',
      decommissioned: 'Decommissioned vehicles',
      pendingLogbooks: 'Logbooks awaiting review',
      missingLogbooks: 'Missing logbooks',
      fuelPending: 'Fuel entries awaiting review',
      expensesPending: 'Expenses awaiting review',
      leasesNearLimit: 'Leases near the km limit',
      dueDates: 'Inspections and insurance',
      incidents: 'Open defects and damage',
      elevatedCosts: 'Elevated costs',
      integration: 'Route integration errors',
    },
    hints: {
      fuelWarnings: '{count} with warnings',
      expensesAmount: 'worth {amount}',
      dueOverdue: '{count} overdue',
      incidentsSerious: '{count} serious',
      incidentsDecision: '{count} awaiting a decision',
      integrationNever: 'Route import has not been run yet',
      integrationFailed: '{count} failed runs (7 days)',
      integrationUnmapped: '{count} routes without a vehicle',
      noLeasesAccess: 'No access to contracts',
      discrepancy: '{count} inconsistent',
      oldest: 'oldest since {date}',
    },
    sections: {
      missing: 'Missing logbooks',
      missingHint: 'Vehicles whose odometer grew without a logbook covering it (window: {days} days).',
      missingEmpty: 'All recent mileage is covered by logbooks.',
      missingKm: '{km} without a logbook',
      due: 'Inspection and insurance due dates',
      dueHint: 'Due dates within {days} days and overdue ones.',
      dueEmpty: 'No upcoming due dates.',
      dueKind: { INSPECTION: 'Inspection', INSURANCE: 'Insurance' },
      dueLeft: 'in {n} days',
      dueToday: 'today',
      dueOverdue: 'overdue by {n} days',
      leases: 'Vehicles near the lease limit',
      leasesHint: 'Contracts past a warning threshold.',
      leasesEmpty: 'No contract has passed a warning threshold.',
      leasesForecast: 'Forecast overage: {km}',
      incidents: 'Defects and damage',
      incidentsEmpty: 'No open reports.',
      elevated: 'Vehicles with elevated costs',
      elevatedHint: 'Operating cost per km (excluding lease instalments) above 1.4 × the group median, last 90 days.',
      elevatedEmpty: 'No vehicle deviates from its group on cost.',
      elevatedRatio: '{ratio}× median ({median})',
      integration: 'Route integration',
      integrationHint: 'CSV file import — the route system is not connected yet.',
      integrationLast: 'Last run: {date}',
      integrationNever: 'No import has been run yet.',
      integrationRejected: 'Rejected rows (7 days): {count}',
      integrationOpen: 'Open integration',
    },
  },
  alerts: {
    title: 'Fleet alerts',
    description: 'Detected problems with evidence and a suggested action. A manually closed alert does not return for 14 days.',
    empty: 'No alerts — the fleet is in good shape.',
    emptyFiltered: 'No alert matches the filters.',
    loadFailed: 'Could not load alerts.',
    filterStatus: 'Alert status',
    filterKind: 'Category',
    filterSeverity: 'Severity',
    allStatuses: 'All statuses',
    allKinds: 'All categories',
    allSeverities: 'Any severity',
    status: { OPEN: 'Open', ACK: 'Acknowledged', RESOLVED: 'Closed' },
    severity: { CRITICAL: 'Critical', WARNING: 'Warning', INFO: 'Info' },
    kind: {
      LEASE_LIMIT: 'Lease km limit',
      LEASE_FORECAST_OVERAGE: 'Forecast limit overage',
      INSPECTION_DUE: 'Inspection due',
      INSURANCE_DUE: 'Insurance due',
      MISSING_LOGBOOK: 'Missing logbook',
      PENDING_LOGBOOK_REVIEW: 'Logbooks awaiting review',
      PENDING_FUEL_REVIEW: 'Fuel entries awaiting review',
      PENDING_EXPENSE_REVIEW: 'Expenses awaiting review',
      INCIDENT_OPEN: 'Open report',
      ELEVATED_COST: 'Elevated costs',
      DRIVER_HOURS_OVER_LIMIT: 'Driver over the hours threshold',
    },
    actions: {
      REVIEW_LEASE_USAGE: 'Check limit usage and plan routes',
      PLAN_LEASE_ANNEX: 'Consider a lease annex',
      SCHEDULE_INSPECTION: 'Book the technical inspection',
      RENEW_INSURANCE: 'Renew the insurance',
      COMPLETE_LOGBOOK: 'Complete the logbook for this mileage',
      REVIEW_LOGBOOKS: 'Review pending logbooks',
      REVIEW_FUEL: 'Review pending fuel entries',
      REVIEW_EXPENSES: 'Review pending expenses',
      DECIDE_INCIDENT: 'Decide whether the vehicle may be driven',
      HANDLE_INCIDENT: 'Pick up the report for handling',
      INVESTIGATE_COSTS: 'Investigate why the vehicle costs are high',
      REVIEW_DRIVER_HOURS: 'Review the driver schedule and spread the routes',
    },
    suggested: 'Suggested action',
    detectedAt: 'Detected {date}',
    period: 'Period: {from} – {to}',
    showEvidence: 'Evidence',
    hideEvidence: 'Hide data',
    evidenceTitle: 'Why this alert',
    related: 'Related items',
    moreRelated: 'and {count} more',
    open: 'Go to details',
    openVehicle: 'Vehicle page',
    noLink: 'No access to the details view.',
    ack: 'Acknowledge',
    acked: 'Acknowledged {date}',
    resolve: 'Close',
    resolved: 'Closed {date}',
    ackDone: 'Alert acknowledged.',
    resolveDone: 'Alert closed.',
    resolveTitle: 'Close this alert?',
    resolveBody: 'The alert will be closed manually and will not return for 14 days, even if the problem persists. Close it only when it is dealt with or you are consciously ignoring it.',
    resolveConfirm: 'Close alert',
    recompute: 'Recompute alerts',
    recomputeDone: 'Alerts recomputed: new {created}, refreshed {updated}, closed {resolved}.',
    pageOf: 'Alerts: {total}',
    evidence: {
      hours: 'Hours in the month',
      threshold: 'Hours threshold',
      overHours: 'Over the threshold',
      contractNumber: 'Contract',
      usedPercent: 'Limit usage',
      thresholdReached: 'Threshold reached',
      drivenKm: 'Kilometres driven',
      limitKm: 'Km limit',
      remainingKm: 'Km remaining',
      exceededKm: 'Km over the limit',
      forecastEndKm: 'Forecast mileage at contract end',
      forecastOverageKm: 'Forecast overage',
      forecastOverageCost: 'Forecast overage cost',
      indicative: 'Indicative forecast',
      dueAt: 'Due date',
      daysLeft: 'Days left',
      overdue: 'Overdue',
      undocumentedKm: 'Mileage without a logbook',
      windowDays: 'Analysis window',
      count: 'Number of items',
      oldestAgeDays: 'Oldest has been waiting',
      thresholdDays: 'Alert threshold',
      totalAmount: 'Total amount',
      reasons: 'Reason',
      incidentType: 'Type',
      severity: 'Report severity',
      status: 'Report status',
      decision: 'Vehicle decision',
      blocksVehicle: 'Blocks the vehicle',
      costPerKm: 'Cost per km',
      peerMedianPerKm: 'Group median per km',
      ratio: 'Multiple of the median',
      km: 'Mileage in the window',
      peerGroup: 'Comparison group',
    },
    reasons: { NO_DECISION: 'No decision on the vehicle', UNHANDLED: 'Report not handled' },
    peer: { TYPE: 'vehicles of the same type', FLEET: 'the whole fleet' },
  },
};
